import React, { useRef, useState } from 'react'
import Alert from '@mui/material/Alert'
import { Flex, Spin } from 'antd'
import { selectCurrentZone } from '../../store/global'
import BusinessesSummary from './BusinessSummary/BusinessSummary'
import { CurrentZone } from 'utils/types'
import { OffshoreEntityForTable } from './types'
import { useVerticalEntitiesQuery } from 'hooks/useVerticalEntitiesQuery'
import useVerticalSearchAndSort from 'hooks/useVerticalSearchAndSort'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import cx from 'classnames'
import messages from './messages'
import { IOffshoreOverviewData } from './BusinessSummary/utils'
import { useOffshoreOverviewQuery } from 'hooks/useOffshoreOverviewQuery'
import { Filter } from 'components/FilterDrawer/FilterDrawer.config'
import { useEmptyColumnsQuery } from 'hooks/useEmptyColumnsQuery'
import { filtersInitialState } from './OffshoreTablePage.config'
import { useFilterOptionsQuery } from 'hooks/useFilterOptionsQuery'
import VerticalTable from 'UI/VerticalTable/VerticalTable'
import VerticalTableFilters from 'components/VerticalTableFilters/VerticalTableFilters'
import ExportCSVTableVertical from 'components/ExportCSVTableVertical/ExportCSVTableVertical'
import Search from 'UI/Search/Search'
import { COLUMNS_IDS } from 'UI/VerticalTable/utils'
import { keysForEmptyColumnsRequest, offshoreColumns } from './utils'
import { PAGE_SIZE } from 'components/verticals/constants'
import styles from 'styles/TablePageCommon.scss'

const OffshoreTablePage = (): JSX.Element => {
  const currentZone: CurrentZone = useSelector(selectCurrentZone)

  const containerRef = useRef<HTMLDivElement | null>(null)
  const statisticsActivityRef = useRef<HTMLDivElement | null>(null)
  const navigationRef = useRef<HTMLDivElement | null>(null)

  const [activeFilters, setActiveFilters] = useState<Filter[]>([])

  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const { searchValue, searchValueDebounced, sorting, setSearchValue, setSorting } = useVerticalSearchAndSort()

  const { data: emptyColumnsData, isFetched: emptyColumnsIsFetched } = useEmptyColumnsQuery({
    verticalIndices: currentZone?.offshoreIndices,
    currentQueryKey: 'offshore-entities-key',
    keys: keysForEmptyColumnsRequest,
  })

  const { data: overviewData } = useOffshoreOverviewQuery<IOffshoreOverviewData>({
    verticalIndices: currentZone?.offshoreIndices,
    currentQueryKey: 'offshore-overview-key',
    searchValue: searchValueDebounced,
    filters: activeFilters,
  })

  const { verticalEntities, fetchNextPage, isFetching, isLoading, isError } =
    useVerticalEntitiesQuery<OffshoreEntityForTable>({
      verticalIndices: currentZone?.offshoreIndices,
      currentQueryKey: 'offshore-entities-key',
      searchValue: searchValueDebounced,
      sorting,
      filters: activeFilters,
      pageSize: PAGE_SIZE,
    })

  const { filterOptions } = useFilterOptionsQuery({
    verticalIndices: currentZone?.offshoreIndices,
    currentQueryKey: 'offshore-filter-options-key',
    filtersInitialState,
  })

  return (
    <div ref={containerRef} className={styles.container}>
      <div ref={navigationRef}>
        <header className='page-header withNavigationHeader'>
          <h1 className={cx('page-title', 'h1')}>
            <FormattedMessage {...messages.header} />
          </h1>
        </header>
      </div>
      <section className={styles.summary} id={'businessSummary'} ref={statisticsActivityRef}>
        <BusinessesSummary overviewData={overviewData} />
      </section>
      {isLoading ? (
        <Flex align='center' gap='middle' justify='center' style={{ padding: '45px 14px' }}>
          <Spin size='large' />
        </Flex>
      ) : isError ? (
        <Alert severity='error' sx={{ fontFamily: 'Source Sans Pro', fontSize: '1rem', alignItems: 'center' }}>
          Something went wrong
        </Alert>
      ) : (
        <>
          {emptyColumnsIsFetched && (
            <VerticalTable
              pageSize={PAGE_SIZE}
              enableColumnPicker
              dataSource={verticalEntities as any[]}
              columns={offshoreColumns}
              containerRef={containerRef}
              additionalRefs={[statisticsActivityRef, navigationRef]}
              fetchNextPage={fetchNextPage}
              total={overviewData?.total || 0}
              loading={isFetching}
              disabledColumns={emptyColumnsData}
              onSelectionChange={setSelectedIds}
              removeFromPickerColumns={[COLUMNS_IDS.profileNames]}
              onRow={() => ({
                style: { cursor: 'default' },
              })}
              onChange={(pagination, filters, sorter) => {
                if (!Array.isArray(sorter)) {
                  if (sorter.order) {
                    setSorting([{ id: sorter.columnKey as string, desc: sorter.order === 'descend' }])
                  } else {
                    setSorting([])
                  }
                }
              }}
            >
              <div className={styles.counter}>
                {`Offshore: `}
                <span>{selectedIds.length > 0 ? `${selectedIds.length} selected` : overviewData?.total}</span>
              </div>
              <VerticalTableFilters
                activeFilters={activeFilters}
                setActiveFilters={setActiveFilters}
                filtersInitialState={filtersInitialState}
                filterOptions={filterOptions}
                filterAdditionalData={[]}
              />
              <Search
                placeholder='input search text'
                enterButton
                allowClear
                style={{ width: 264 }}
                loading={isFetching}
                onSearch={newSearchValue => setSearchValue(newSearchValue)}
              />
              <ExportCSVTableVertical
                zoneIndex={currentZone?.offshoreIndices}
                csvFileName='Offshore'
                searchValue={searchValue}
                selectedIds={selectedIds}
                sorting={sorting}
              />
            </VerticalTable>
          )}
        </>
      )}
    </div>
  )
}

export default OffshoreTablePage
